import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { jsxs, jsx } from 'react/jsx-runtime';
import React, { useRef, useState, useCallback } from 'react';
import { useSelect, Popper, SelectProvider } from '@mui/base';
import { unstable_useForkRef } from '@mui/utils';
import classnames from 'classnames';
import { useConfigContextLoose, TextLabel, Flex, Body } from '@cognite/cogs-core';
import { getStatusIcon, ClearAllIcon, ChevronUpIcon, ChevronDownIcon } from '@cognite/cogs-icons';
import { IconWrapper } from '@cognite/cogs-utils';
import { Option } from './option.js';

var SelectToggleContent = function SelectToggleContent(_ref) {
  var getOptionMetadata = _ref.getOptionMetadata,
    placeholder = _ref.placeholder,
    renderValue = _ref.renderValue,
    value = _ref.value;
  if (!value || Array.isArray(value) && value.length === 0) {
    return jsx(Body, {
      className: "cogs-lab-select-toggle-content",
      muted: true,
      size: "medium",
      noOfLines: 1,
      children: placeholder
    });
  }
  var selectedOptionsMetadata;
  if (Array.isArray(value)) {
    selectedOptionsMetadata = value.map(function (v) {
      return getOptionMetadata(v);
    }).filter(function (l) {
      return !!l;
    });
  } else if (value !== undefined) {
    selectedOptionsMetadata = getOptionMetadata(value);
  }
  if (renderValue) {
    return jsx(Body, {
      className: "cogs-lab-select-toggle-content",
      size: "medium",
      children: renderValue(selectedOptionsMetadata)
    });
  }
  var toggleLabel = Array.isArray(selectedOptionsMetadata) ? selectedOptionsMetadata.map(function (option) {
    return option.label;
  }).filter(function (l) {
    return !!l;
  }).join(', ') : selectedOptionsMetadata === null || selectedOptionsMetadata === undefined ? undefined : selectedOptionsMetadata.label;
  return jsx(Body, {
    className: "cogs-lab-select-toggle-content",
    size: "medium",
    children: toggleLabel
  });
};
/**
 * Select component is used for collecting user provided information from a list of options.
 */
var Select = /*#__PURE__*/React.forwardRef(function (_ref2, forwardedRef) {
  var areOptionsEqual = _ref2.areOptionsEqual,
    children = _ref2.children,
    id = _ref2.id,
    className = _ref2.className,
    containerRef = _ref2.containerRef,
    _ref2$defaultListboxO = _ref2.defaultListboxOpen,
    defaultListboxOpen = _ref2$defaultListboxO === undefined ? false : _ref2$defaultListboxO,
    defaultValue = _ref2.defaultValue,
    disabledProp = _ref2.disabled,
    fullWidth = _ref2.fullWidth,
    getSerializedValue = _ref2.getSerializedValue,
    getOptionAsString = _ref2.getOptionAsString,
    label = _ref2.label,
    listboxId = _ref2.listboxId,
    listboxOpenProp = _ref2.listboxOpen,
    listboxProps = _ref2.listboxProps,
    multiple = _ref2.multiple,
    name = _ref2.name,
    required = _ref2.required,
    onChange = _ref2.onChange,
    onListboxOpenChange = _ref2.onListboxOpenChange,
    options = _ref2.options,
    _ref2$placeholder = _ref2.placeholder,
    placeholder = _ref2$placeholder === undefined ? 'Select an option' : _ref2$placeholder,
    popperProps = _ref2.popperProps,
    renderOption = _ref2.renderOption,
    renderValue = _ref2.renderValue,
    _ref2$size = _ref2.size,
    size = _ref2$size === undefined ? 'medium' : _ref2$size,
    valueProp = _ref2.value,
    _ref2$variant = _ref2.variant,
    variant = _ref2$variant === undefined ? 'outline' : _ref2$variant,
    ariaLabel = _ref2['aria-label'],
    trailingContent = _ref2.trailingContent,
    clearable = _ref2.clearable,
    icon = _ref2.icon,
    status = _ref2.status,
    statusText = _ref2.statusText;
  var _a, _b, _c;
  var shouldDisplayLabel = !!label && variant !== 'ghost';
  var listboxRef = useRef(null);
  var buttonRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isButtonDefined = _useState2[0],
    setIsButtonDefined = _useState2[1];
  var handleButtonRefChange = useCallback(function (element) {
    setIsButtonDefined(element != null);
  }, []);
  var handleButtonRef = unstable_useForkRef(forwardedRef, buttonRef, handleButtonRefChange);
  var _useSelect = useSelect({
      areOptionsEqual: areOptionsEqual,
      buttonRef: handleButtonRef,
      componentName: 'Select',
      defaultOpen: defaultListboxOpen,
      defaultValue: defaultValue,
      disabled: disabledProp,
      getOptionAsString: getOptionAsString,
      getSerializedValue: getSerializedValue,
      listboxId: listboxId,
      listboxRef: listboxRef,
      multiple: multiple,
      name: name,
      onChange: onChange,
      onOpenChange: onListboxOpenChange,
      open: listboxOpenProp,
      required: required,
      value: valueProp
    }),
    contextValue = _useSelect.contextValue,
    disabled = _useSelect.disabled,
    getButtonProps = _useSelect.getButtonProps,
    getListboxProps = _useSelect.getListboxProps,
    getOptionMetadata = _useSelect.getOptionMetadata,
    open = _useSelect.open,
    value = _useSelect.value,
    dispatch = _useSelect.dispatch;
  var isValueNotEmpty = !multiple && value || multiple && Array.isArray(value) && value.length > 0;
  var StatusIcon = getStatusIcon(status);
  var _useConfigContextLoos = useConfigContextLoose(),
    getOverlayContainer = _useConfigContextLoos.getOverlayContainer;
  return jsxs("div", {
    className: classnames('cogs-lab', 'cogs-lab-select', fullWidth ? 'cogs-lab-select-full-width' : 'cogs-lab-select-default-width', disabled && 'cogs-lab-select--disabled', className),
    ref: containerRef,
    "aria-disabled": disabled,
    children: [shouldDisplayLabel && (typeof label === 'string' ? jsx(TextLabel, {
      htmlFor: id,
      text: label
    }) : jsx(TextLabel, Object.assign({
      htmlFor: id
    }, label))), jsxs("button", Object.assign({
      id: id,
      className: classnames('cogs-lab-select-toggle', "cogs-lab-select-toggle--size-".concat(size), "cogs-lab-select-toggle--variant-".concat(variant), "cogs-lab-select-toggle--status-".concat(status), {
        'cogs-lab-select-toggle--opened': open
      }),
      type: "button",
      "aria-label": ariaLabel
    }, getButtonProps(), {
      disabled: disabled,
      "aria-disabled": disabled,
      children: [icon && jsx("div", {
        className: classnames('cogs-lab-select__left-adornment-icon', {
          'cogs-lab-select__left-adornment-icon--disabled': disabled
        }),
        children: jsx(IconWrapper, {
          size: 16,
          children: icon
        })
      }), jsx(SelectToggleContent, {
        getOptionMetadata: getOptionMetadata,
        placeholder: placeholder,
        renderValue: renderValue,
        value: value
      }), clearable && isValueNotEmpty && !disabled && jsx(IconWrapper, {
        className: "cogs-lab-select__clear-icon",
        size: 16,
        onClick: function onClick(e) {
          e.stopPropagation();
          dispatch({
            type: 'list:clearSelection'
          });
        },
        children: jsx(ClearAllIcon, {})
      }), trailingContent && jsx(Flex, {
        direction: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
        children: trailingContent
      }), jsx(IconWrapper, {
        "aria-hidden": true,
        className: classnames('cogs-lab-select-toggle__icon', {
          'cogs-lab-select-toggle__icon--disabled': disabled
        }),
        size: 16,
        children: open ? jsx(ChevronUpIcon, {}) : jsx(ChevronDownIcon, {})
      })]
    })), status && statusText && jsxs("div", {
      className: "cogs-lab-select__status-text-container",
      children: [StatusIcon && jsx(IconWrapper, {
        size: 12,
        children: jsx(StatusIcon, {
          className: classnames('cogs-lab-select__status-text', "cogs-lab-select__status-text--status-".concat(status))
        })
      }), jsx(Body, {
        size: "x-small",
        className: classnames('cogs-lab-select__status-text', "cogs-lab-select__status-text--status-".concat(status)),
        children: statusText
      })]
    }), isButtonDefined && jsx(Popper, Object.assign({}, popperProps, {
      onKeyDown: function onKeyDown(e) {
        // this is required to prevent modal from closing if select is
        // rendered inside it and closed via escape
        if (e.key === 'Escape') e.stopPropagation();
      },
      anchorEl: buttonRef.current,
      className: classnames('cogs-lab', 'cogs-lab-select-popper', fullWidth ? 'cogs-lab-select-full-width' : 'cogs-lab-select-default-width', className),
      keepMounted: true,
      open: open,
      container: (popperProps === null || popperProps === undefined ? undefined : popperProps.container) || (getOverlayContainer === null || getOverlayContainer === undefined ? undefined : getOverlayContainer()),
      placement: (popperProps === null || popperProps === undefined ? undefined : popperProps.placement) || 'bottom-start',
      role: (popperProps === null || popperProps === undefined ? undefined : popperProps.role) || 'dialog',
      style: {
        width: (_a = buttonRef.current) === null || _a === undefined ? undefined : _a.clientWidth
      },
      modifiers: [{
        name: 'offset',
        options: {
          offset: [0, 6] // this is 4 in design but focus ring takes 2px so adding 6 here
        }
      }].concat(_toConsumableArray((_b = popperProps === null || popperProps === undefined ? undefined : popperProps.modifiers) !== null && _b !== undefined ? _b : [])),
      children: jsx("ul", Object.assign({}, getListboxProps(), {
        className: "cogs-lab-select-listbox",
        style: {
          maxHeight: listboxProps === null || listboxProps === undefined ? undefined : listboxProps.maxHeight
        },
        children: jsx(SelectProvider, {
          value: contextValue,
          children: (_c = options === null || options === undefined ? undefined : options.map(function (option) {
            var _a;
            return jsx(Option, {
              disabled: option.disabled,
              label: option.label,
              value: option.value,
              children: (_a = renderOption === null || renderOption === undefined ? undefined : renderOption(option)) !== null && _a !== undefined ? _a : option.label
            }, option.label);
          })) !== null && _c !== undefined ? _c : children
        })
      }))
    }))]
  });
}); // we need to cast the type to make `Select` a generic component since `forwardRef` doesn't support it by default

export { Select };
